import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { EntityFillingService } from './entity-filling.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import moment from 'moment';

@Component({
    selector: 'gtw-entity-filling-ce',
    templateUrl: './entity-filling.component.html',
    styleUrls: ['./entity-filling.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityFillingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  filterParams: any = {};
  rowData: any = {};
  baseURLs: any = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  // Component properties
  modalTitle = "Filing Dates Setup";
  showEntityLoader = false;
  isSaving = false;
  isSavingUpdate = false;
  isSavingRemove = false;
  isSavingDelete = false;
  crudLoading = false;
  dateOptions = {};
  currentDate = new Date();
  isUpdateMode = false;
  minimise = false;
  
  // Date picker constraints
  minYear: string = '';
  maxYear: string = '';
  
  // Form data
  entityFiling: any = {
    TAX_YEAR: '',
    SCENARIO_TYPE_CODE: '',
    SCENARIO_DESC: '',
    GROUP_OBJ_KEY: '',
    LEGAL_ENTITY_ID: '',
    FILING_CYCLE: '',
    SCENARIO: '',
    TAX_YEAR_BEGIN: '',
    TAX_YEAR_END: '',
    ACTIVE_Y_N: 'Y',
    ROLLFWD_Y_N: 'Y'
  };

  entityFilingBackup: any = {};

  // Dropdown data
  allscenarioTypeCode: any[] = [];
  allscenarioDesc: any[] = [];
  allGroupObjByYear: any[] = [];
  allLeids: any[] = [];
  allFilingCycleTypes: any[] = [];
  allAppScenarios: any[] = [];

  // Global services
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  constructor(
    private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
    private jsonObjectFactoryService: JsonObjectFactoryService,
    private entityFillingService: EntityFillingService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  initializeYearConstraints(): void {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 10; // 10 years back
    const endYear = currentYear + 10;   // 10 years forward
    
    // Set min and max year constraints for date inputs
    this.minYear = `${startYear}-01-01`;
    this.maxYear = `${endYear}-12-31`;
  }

  init(): void {
    console.log('EntityFillingComponent init() called');
    console.log('baseURLs:', this.baseURLs);
    console.log('filterParams:', this.filterParams);
    console.log('rowData:', this.rowData);
    console.log('GENERAL_CONFIG:', this.GENERAL_CONFIG);

    this.initializeYearConstraints();
    
    // Check if we have rowData for update mode
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.isUpdateMode = true;
      this.entityFiling = _.cloneDeep(this.rowData);
      
      // Store backup with original dates for reset functionality
      this.entityFilingBackup = _.cloneDeep(this.rowData);
      
      // Convert dates to date picker format (YYYY-MM-DD) for update mode
      // Ensure we use the values from rowData directly
      const taxYearBeginRaw = this.rowData.TAX_YEAR_BEGIN || this.entityFiling.TAX_YEAR_BEGIN || '';
      const taxYearEndRaw = this.rowData.TAX_YEAR_END || this.entityFiling.TAX_YEAR_END || '';
      
      console.log('Update mode - raw dates from rowData:', {
        TAX_YEAR_BEGIN_RAW: taxYearBeginRaw,
        TAX_YEAR_END_RAW: taxYearEndRaw,
        rowDataKeys: Object.keys(this.rowData)
      });
      
      // Convert dates and ensure they're set
      if (taxYearBeginRaw) {
        const convertedBegin = this.convertToDatePickerFormat(taxYearBeginRaw);
        this.entityFiling.TAX_YEAR_BEGIN = convertedBegin;
        console.log('Converted TAX_YEAR_BEGIN:', taxYearBeginRaw, '->', convertedBegin);
      } else {
        this.entityFiling.TAX_YEAR_BEGIN = '';
        console.warn('TAX_YEAR_BEGIN is empty in rowData');
      }
      
      if (taxYearEndRaw) {
        const convertedEnd = this.convertToDatePickerFormat(taxYearEndRaw);
        this.entityFiling.TAX_YEAR_END = convertedEnd;
        console.log('Converted TAX_YEAR_END:', taxYearEndRaw, '->', convertedEnd);
      } else {
        this.entityFiling.TAX_YEAR_END = '';
        console.warn('TAX_YEAR_END is empty in rowData');
      }
      
      console.log('Update mode - final entityFiling dates:', {
        TAX_YEAR_BEGIN: this.entityFiling.TAX_YEAR_BEGIN,
        TAX_YEAR_END: this.entityFiling.TAX_YEAR_END
      });
    } else {
      this.isUpdateMode = false;
      this.initializeCreateMode();
    }
    
    // Only proceed if we have all required data
    if (this.GENERAL_CONFIG && 
        this.GENERAL_CONFIG.base_url &&
        this.filterParams && 
        Object.keys(this.filterParams).length > 0) {
      console.log('All required data available, calling loadInitialData()');
      this.loadInitialData();
    } else {
      console.log('Missing required data, not calling loadInitialData()');
    }
  }

  initializeCreateMode(): void {
    const taxYear = this.filterParams?.tax_year || new Date().getFullYear().toString();
    this.entityFiling.TAX_YEAR = taxYear;
    this.entityFiling.FILING_CYCLE = "CYE";
    this.entityFiling.TAX_YEAR_BEGIN = this.convertToDatePickerFormat("01/01/" + taxYear);
    this.entityFiling.TAX_YEAR_END = this.convertToDatePickerFormat("12/31/" + taxYear);
    this.entityFiling.ACTIVE_Y_N = "Y";
    this.entityFiling.ROLLFWD_Y_N = "Y";
  }

  loadInitialData(): void {
    this.showEntityLoader = true;
    
    // Set a timeout to ensure loader is turned off even if there are issues
    const timeoutId = setTimeout(() => {
      if (this.showEntityLoader) {
        console.warn('Data loading timeout - turning off loader');
        this.showEntityLoader = false;
        this.cdr.markForCheck();
        this.gtwSnackbarService.openSnackBar('Data loading timeout - please try again', 'gtw-snackbar--red');
      }
    }, 10000); // 10 second timeout
    
    try {
      // Load data based on mode
      if (this.isUpdateMode) {
        // For update mode, only load filing cycle types
        this.entityFillingService.loadAllFilingCycleType(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData)
          .then(filingCycleTypes => {
            clearTimeout(timeoutId);
            this.allFilingCycleTypes = filingCycleTypes;
            this.showEntityLoader = false;
            this.cdr.markForCheck();
          })
          .catch(error => {
            clearTimeout(timeoutId);
            console.error('Error loading filing cycle types:', error);
            this.gtwSnackbarService.openSnackBar('Error loading filing cycle types', 'gtw-snackbar--red');
            this.showEntityLoader = false;
            this.cdr.markForCheck();
          });
      } else {
        // For create mode, load all required data
        Promise.all([
          this.entityFillingService.loadScenarioTypeCode(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData),
          this.entityFillingService.loadAllFilingCycleType(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData),
          this.entityFillingService.loadAllAppScenario(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData)
        ]).then(([scenarioTypeCodes, filingCycleTypes, appScenarios]) => {
          clearTimeout(timeoutId);
          this.allscenarioTypeCode = scenarioTypeCodes;
          this.allFilingCycleTypes = filingCycleTypes;
          this.allAppScenarios = appScenarios;
          this.showEntityLoader = false;
          this.cdr.markForCheck();
        }).catch(error => {
          clearTimeout(timeoutId);
          console.error('Error loading initial data:', error);
          this.gtwSnackbarService.openSnackBar('Error loading initial data', 'gtw-snackbar--red');
          this.showEntityLoader = false;
          this.cdr.markForCheck();
        });
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Error in loadInitialData:', error);
      this.gtwSnackbarService.openSnackBar('Error initializing component', 'gtw-snackbar--red');
      this.showEntityLoader = false;
      this.cdr.markForCheck();
    }
  }

  onScenarioTypeCodeChange(): void {
    const selectedScenarioTypeCode = this.entityFiling.SCENARIO_TYPE_CODE;
    if (selectedScenarioTypeCode) {
      this.entityFillingService.loadScenarioDesc(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData, selectedScenarioTypeCode)
        .then(scenarioDescs => {
          this.allscenarioDesc = scenarioDescs;
          this.entityFiling.GROUP_OBJ_KEY = '';
          this.entityFiling.SCENARIO_DESC = '';
        })
        .catch(error => console.error('Error loading scenario descriptions:', error));
    }
  }

  onPopulateGroupObjYear(): void {
    const scenarioTypeCode = this.entityFiling.SCENARIO_TYPE_CODE;
    const scenarioDesc = this.entityFiling.SCENARIO_DESC;
    if (scenarioTypeCode && scenarioDesc) {
      this.entityFillingService.loadGroupObjKey(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData, scenarioTypeCode, scenarioDesc)
        .then(groupObjs => {
          this.allGroupObjByYear = groupObjs;
          this.entityFiling.GROUP_OBJ_KEY = '';
        })
        .catch(error => console.error('Error loading group object key:', error));
    }
  }

  onPopulateFilingLEID(): void {
    const groupObjKey = this.entityFiling.GROUP_OBJ_KEY;
    if (groupObjKey) {
      this.entityFillingService.loadAllEntities(this.GENERAL_CONFIG.base_url, groupObjKey)
        .then(leids => {
          this.allLeids = leids;
          this.entityFiling.LEGAL_ENTITY_ID = '';
        })
        .catch(error => console.error('Error loading filing LE IDs:', error));
    }
  }

  onPopulateTaxDate(): void {
    if (this.entityFiling.FILING_CYCLE === "CYE") {
      this.entityFiling.TAX_YEAR_BEGIN = this.convertToDatePickerFormat("01/01/" + this.entityFiling.TAX_YEAR);
      this.entityFiling.TAX_YEAR_END = this.convertToDatePickerFormat("12/31/" + this.entityFiling.TAX_YEAR);
    } else {
      this.entityFiling.TAX_YEAR_BEGIN = this.convertToDatePickerFormat("10/01/" + this.entityFiling.TAX_YEAR);
      this.entityFiling.TAX_YEAR_END = this.convertToDatePickerFormat("09/30/" + (Number(this.entityFiling.TAX_YEAR) + 1));
    }
  }

  onTaxYearBeginChange(): void {
    const dateValue = this.entityFiling.TAX_YEAR_BEGIN;
    if (dateValue) {
      // Keep the date in YYYY-MM-DD format for the date picker
      // The conversion to MM/DD/YYYY will happen in buildSaveData/buildUpdateData
      console.log('Tax Year Begin changed to:', dateValue);
    }
  }

  onTaxYearEndChange(): void {
    const dateValue = this.entityFiling.TAX_YEAR_END;
    if (dateValue) {
      // Keep the date in YYYY-MM-DD format for the date picker
      // The conversion to MM/DD/YYYY will happen in buildSaveData/buildUpdateData
      console.log('Tax Year End changed to:', dateValue);
    }
  }

  convertToDatePickerFormat(dateString: string | Date | any): string {
    if (!dateString) return '';
    
    // Handle Date object
    if (dateString instanceof Date) {
      return moment(dateString).format('YYYY-MM-DD');
    }
    
    // Handle string, number, or other types
    if (typeof dateString !== 'string') {
      dateString = String(dateString);
    }
    
    // Trim whitespace
    dateString = dateString.trim();
    
    // Check if already in YYYY-MM-DD format (exact pattern match)
    const yyyyMmDdPattern = /^\d{4}-\d{2}-\d{2}$/;
    if (yyyyMmDdPattern.test(dateString)) {
      console.log('Date already in YYYY-MM-DD format:', dateString);
      return dateString;
    }
    
    // Try MM/DD/YYYY format first (most common from API)
    if (dateString.includes('/')) {
      const mmddyyyyDate = moment(dateString, 'MM/DD/YYYY', true);
      if (mmddyyyyDate.isValid()) {
        const formattedDate = mmddyyyyDate.format('YYYY-MM-DD');
        console.log('Converting date from MM/DD/YYYY:', dateString, 'to', formattedDate);
        return formattedDate;
      }
      
      // Try DD/MM/YYYY as fallback
      const ddmmyyyyDate = moment(dateString, 'DD/MM/YYYY', true);
      if (ddmmyyyyDate.isValid()) {
        const formattedDate = ddmmyyyyDate.format('YYYY-MM-DD');
        console.log('Converting date from DD/MM/YYYY:', dateString, 'to', formattedDate);
        return formattedDate;
      }
    }
    
    // Try parsing with moment (handles various formats)
    const momentDate = moment(dateString);
    if (momentDate.isValid()) {
      const formattedDate = momentDate.format('YYYY-MM-DD');
      console.log('Converting date (generic parse):', dateString, 'to', formattedDate);
      return formattedDate;
    }
    
    console.warn('Could not convert date:', dateString);
    return '';
  }

  convertToApiFormat(dateString: string): string {
    if (!dateString) return '';
    
    // If it's in YYYY-MM-DD format (from date picker), convert to MM/DD/YYYY for API
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const formattedDate = moment(dateString).format('MM/DD/YYYY');
      console.log('Converting date from', dateString, 'to', formattedDate);
      return formattedDate;
    }
    
    // If it's already in MM/DD/YYYY format, return as is
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    
    return '';
  }

  upper(filingCycle: string): void {
    this.entityFiling.FILING_CYCLE = filingCycle.toUpperCase();
  }

  isValid(): boolean {
    let requiredFields: string[] = [];
    
    if (this.isUpdateMode) {
      requiredFields = [
        'FILING_CYCLE', 'TAX_YEAR_BEGIN', 'TAX_YEAR_END'
      ];
    } else {
      requiredFields = [
        'TAX_YEAR', 'SCENARIO_TYPE_CODE', 'SCENARIO_DESC', 'GROUP_OBJ_KEY',
        'LEGAL_ENTITY_ID', 'FILING_CYCLE', 'SCENARIO', 'TAX_YEAR_BEGIN', 'TAX_YEAR_END'
      ];
    }

    for (let field of requiredFields) {
      if (!this.entityFiling[field]) {
        this.entityFiling[field + '_NOT_VALID'] = true;
        return false;
      }
    }
    return true;
  }

  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    if (this.isUpdateMode) {
      this.updateEntityFiling();
    } else {
      this.createEntityFiling();
    }
  }

  createEntityFiling(): void {
    this.crudLoading = true;
    
    // Build save data with real filterParams values
    const saveData = this.buildSaveData();
    
    // 🚀 CRITICAL: Pass filterParams and rowData to service
    this.entityFillingService.saveEntityFilingForm(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.entityFiling
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('EC Filing Dates created successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  updateEntityFiling(): void {
    this.isSavingUpdate = true;
    
    // Build update data
    const updateData = this.buildUpdateData();
    
    // 🚀 CRITICAL: Pass filterParams and rowData to service
    this.entityFillingService.updateEntityFilingForm(
      this.GENERAL_CONFIG.base_url,
      updateData,
      this.globalService,
      this.filterParams,
      this.entityFiling
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSavingUpdate = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('EC Filing Dates updated successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSavingUpdate = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while updating. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  removeEntityFiling(): void {
    this.isSavingRemove = true;
    
    this.entityFillingService.removeEntityFilingForm(
      this.GENERAL_CONFIG.base_url,
      this.entityFiling.TAX_YEAR,
      this.entityFiling.GROUP_OBJ_KEY,
      this.entityFiling.LEGAL_ENTITY_ID,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSavingRemove = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('Filing Date Setup removed successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSavingRemove = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while removing. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  deleteEntityFiling(): void {
    this.isSavingDelete = true;
    
    this.entityFillingService.deleteEntityFilingForm(
      this.GENERAL_CONFIG.base_url,
      this.entityFiling.TAX_YEAR,
      this.entityFiling.GROUP_OBJ_KEY,
      this.entityFiling.LEGAL_ENTITY_ID,
      this.globalService?.globalParams ?? {},
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSavingDelete = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('Filing Dates Setup deleted successfully', 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSavingDelete = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  buildSaveData(): any {
    const taxYear =
      this.entityFiling.TAX_YEAR ??
      this.filterParams?.tax_year ??
      this.rowData?.TAX_YEAR ??
      this.globalService?.globalParams?.tax_year;
    const scenario =
      this.entityFiling.SCENARIO ??
      this.filterParams?.scenario ??
      this.rowData?.SCENARIO ??
      this.globalService?.globalParams?.scenario;
    const jcdKey =
      this.filterParams?.jcd_key ??
      this.rowData?.JCD_KEY ??
      this.globalService?.globalParams?.jcd_key;

    const jsonData = {
      TAX_YEAR: taxYear,
      ENTITY_ID: this.entityFiling.ENTITY_ID || '',
      GROUP_OBJ_KEY: this.entityFiling.GROUP_OBJ_KEY || '',
      SCENARIO: scenario,
      LEGAL_ENTITY_ID: this.entityFiling.LEGAL_ENTITY_ID || '',
      TAX_YEAR_BEGIN: this.convertToApiFormat(this.entityFiling.TAX_YEAR_BEGIN) || '',
      TAX_YEAR_END: this.convertToApiFormat(this.entityFiling.TAX_YEAR_END) || '',
      FILING_CYCLE: this.entityFiling.FILING_CYCLE || '',
      ACTIVE_Y_N: this.entityFiling.ACTIVE_Y_N || 'Y',
      ROLLFWD_Y_N: this.entityFiling.ROLLFWD_Y_N || 'Y',
      SCENARIO_TYPE_CODE: this.entityFiling.SCENARIO_TYPE_CODE || '',
      SCENARIO_DESC: this.entityFiling.SCENARIO_DESC || '',
      tax_year: taxYear
    };

    return {
      jsonSettings: JSON.stringify({ tax_year: taxYear }),
      json_data: JSON.stringify(jsonData),
      tax_year: taxYear,
      screen_key: this.filterParams?.screen_key ?? this.globalService?.globalParams?.screen_key ?? '',
      scenario,
      jcd_key: jcdKey,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.globalParams?.jcd_ta_key ?? '',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.globalParams?.activity_key ?? '',
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.globalParams?.issue_id ?? '',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.globalParams?.issue_short_desc ?? '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save ?? this.globalService?.globalParams?.issue_id_for_tx_save ?? '',
      is_issue_key_enabled: 'N',
      scenarioCode:
        this.filterParams?.scenarioCode ??
        this.filterParams?.scenario_code ??
        this.globalService?.globalParams?.scenarioCode ??
        this.globalService?.globalParams?.scenario_code ??
        'RAF'
    };
  }

  buildUpdateData(): any {
    const taxYear =
      this.entityFiling.TAX_YEAR ??
      this.filterParams?.tax_year ??
      this.rowData?.TAX_YEAR ??
      this.globalService?.globalParams?.tax_year;
    const scenario =
      this.entityFiling.SCENARIO ??
      this.filterParams?.scenario ??
      this.rowData?.SCENARIO ??
      this.globalService?.globalParams?.scenario;
    const jcdKey =
      this.filterParams?.jcd_key ??
      this.rowData?.JCD_KEY ??
      this.globalService?.globalParams?.jcd_key;

    const jsonData = {
      GROUP_OBJ_KEY: this.entityFiling.GROUP_OBJ_KEY || '',
      TAX_YEAR: taxYear,
      SCENARIO: scenario,
      LEGAL_ENTITY_ID: this.entityFiling.LEGAL_ENTITY_ID || '',
      TAX_YEAR_BEGIN: this.convertToApiFormat(this.entityFiling.TAX_YEAR_BEGIN) || '',
      TAX_YEAR_END: this.convertToApiFormat(this.entityFiling.TAX_YEAR_END) || '',
      FILING_CYCLE: this.entityFiling.FILING_CYCLE || '',
      TRADITIONAL_START_DATE: this.entityFiling.TRADITIONAL_START_DATE || '',
      ACTIVE_Y_N: this.entityFiling.ACTIVE_Y_N || 'Y',
      ROLLFWD_Y_N: this.entityFiling.ROLLFWD_Y_N || 'Y',
      object_id: this.entityFiling.object_id || this.rowData?.object_id || '',
      SCENARIO_TYPE_CODE: this.entityFiling.SCENARIO_TYPE_CODE || '',
      tax_year: taxYear
    };

    return {
      jsonSettings: JSON.stringify({ tax_year: taxYear }),
      json_data: JSON.stringify(jsonData),
      tax_year: taxYear,
      screen_key: this.filterParams?.screen_key ?? this.globalService?.globalParams?.screen_key ?? '',
      scenario,
      jcd_key: jcdKey,
      jcd_ta_key: this.filterParams?.jcd_ta_key ?? this.globalService?.globalParams?.jcd_ta_key ?? '',
      activity_key: this.filterParams?.activity_key ?? this.globalService?.globalParams?.activity_key ?? '',
      screen_type: 'grid',
      is_locked: 'N',
      issue_id: this.filterParams?.issue_id ?? this.globalService?.globalParams?.issue_id ?? '',
      issue_short_desc: this.filterParams?.issue_short_desc ?? this.globalService?.globalParams?.issue_short_desc ?? '',
      issue_id_for_tx_save: this.filterParams?.issue_id_for_tx_save ?? this.globalService?.globalParams?.issue_id_for_tx_save ?? '',
      is_issue_key_enabled:
        this.filterParams?.is_issue_key_enabled ??
        this.globalService?.globalParams?.is_issue_key_enabled ??
        'N',
      scenarioCode:
        this.filterParams?.scenarioCode ??
        this.filterParams?.scenario_code ??
        this.globalService?.globalParams?.scenarioCode ??
        this.globalService?.globalParams?.scenario_code ??
        ''
    };
  }

  reset(): void {
    if (this.isUpdateMode) {
      // Reset from backup
      this.entityFiling = _.cloneDeep(this.entityFilingBackup);
      
      // Ensure dates are converted to date picker format
      const taxYearBeginRaw = this.entityFiling.TAX_YEAR_BEGIN || this.entityFilingBackup.TAX_YEAR_BEGIN || '';
      const taxYearEndRaw = this.entityFiling.TAX_YEAR_END || this.entityFilingBackup.TAX_YEAR_END || '';
      
      if (taxYearBeginRaw) {
        this.entityFiling.TAX_YEAR_BEGIN = this.convertToDatePickerFormat(taxYearBeginRaw);
      }
      if (taxYearEndRaw) {
        this.entityFiling.TAX_YEAR_END = this.convertToDatePickerFormat(taxYearEndRaw);
      }
    } else {
      this.entityFiling = {
        TAX_YEAR: this.filterParams?.tax_year || new Date().getFullYear().toString(),
        SCENARIO_TYPE_CODE: '',
        SCENARIO_DESC: '',
        GROUP_OBJ_KEY: '',
        LEGAL_ENTITY_ID: '',
        FILING_CYCLE: '',
        SCENARIO: '',
        TAX_YEAR_BEGIN: '',
        TAX_YEAR_END: '',
        ACTIVE_Y_N: 'Y',
        ROLLFWD_Y_N: 'Y'
      };
      this.initializeCreateMode();
    }
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-entity-filling-ce')) {
      document.querySelector('gtw-entity-filling-ce')?.dispatchEvent(updateEvent);
    }
  }

}
